import type { DehydratedState } from '@tanstack/react-query';
import type { History } from 'history';
import { i18n, InitOptions } from 'i18next';
import type { ComponentType } from 'react';
import { ISagaModule } from 'redux-dynamic-modules-saga';

import { ABConfiguration, AemConfiguration, MainWebShopConfiguration } from './AemConfigurationTypes';
import { I18nTranslationObject, StringStringArrayMap, StringTMap } from './GeneralTypes';
import { FilterState, ProductSearchObject } from './product-lister';
import { ProductGroupById } from './ProductGroupTypes';
import { IStoreConfigurator } from './redux';
import { UrlState } from './UrlTypes';
import { AsNumeralInterface, i18nBackendAjaxOverride } from './vendor';

export enum AEMScenes {
  BOOTSTRAP = 'AEMScenes_Bootstrap',
  ABTESTING = 'AEMScenes_ABTesting',
  ACCOUNT_ACTIVATION = 'AEMScenes_AccountActivation',
  ACCOUNT_CREDIT_CARD = 'AEMScenes_AccountCreditCard',
  ACCOUNT_DELIVERY_ADDRESSES = 'AEMScenes_AccountDeliveryAddresses',
  ACCOUNT_MENU = 'AEMScenes_AccountMenu',
  ACCOUNT_MENU_ITEM = 'AEMScenes_AccountMenuItem',
  ACCOUNT_ORDER_HISTORY = 'AEMScenes_AccountOrderHistory',
  ACCOUNT_OVERVIEW_INFO = 'AEMScenes_AccountOverview_info',
  ACCOUNT_PASSWORD_RESET = 'AEMScenes_AccountPasswordReset',
  ACCOUNT_PAYBACK_ENRICHMENT = 'AEMScenes_AccountPaybackEnrichment',
  ACCOUNT_PREFERENCES = 'AEMScenes_AccountPreferences',
  ACCOUNT_PROFILE_DETAILS = 'AEMScenes_AccountProfileDetails',
  ACCOUNT_REGISTRATION = 'AEMScenes_AccountRegistration',
  ACCOUNT_UNSUBSCRIBE = 'AEMScenes_AccountUnsubscribe',
  ACCOUNT_VOUCHERS = 'AEMScenes_AccountVouchers',
  BASKET_MENU_ITEM = 'AEMScenes_BasketMenuItem',
  BASKET_OVERVIEW_V2 = 'AEMScenes_BasketOverview_v2',
  BASKET_PERSONAL_SHOPPING_ADVICE = 'AEMScenes_BasketPersonalShoppingAdvice',
  BOXED_TITLE = 'AEMScenes_BoxedTitle',
  BRAND_TILE_LISTER = 'AEMScenes_BrandTileLister',
  BREADCRUMB = 'AEMScenes_Breadcrumb',
  CHECKOUT_CONFIRMATION = 'AEMScenes_CheckoutConfirmation',
  BUY_PAYMENT_REQUEST = 'AEMScenes_BuyPaymentRequest',
  CHECKOUT_PAYMENT_REQUEST_CONFIRMATION = 'AEMScenes_CheckoutPaymentRequestConfirmation',
  CAMPAIGN_GROUP = 'AEMScenes_CampaignGroup',
  CATEGORY_TILE = 'AEMScenes_CategoryTile',
  CHANGE_PASSWORD = 'AEMScenes_ChangePassword',
  CHECKOUT = 'AEMScenes_Checkout',
  CHECKOUT_HEADER = 'AEMScenes_CheckoutHeader',
  COLLAPSIBLE_LIST = 'AEMScenes_CollapsibleList',
  CONTENT_TILE_LISTER = 'AEMScenes_ContentTileLister',
  CONTENT_DETAIL_HEADER = 'AEMScenes_ContentDetailHeader',
  COOKIE_MESSAGE = 'AEMScenes_CookieMessage',
  COUNTDOWN = 'AEMScenes_Countdown',
  CURRENCY_MENU_ITEM = 'AEMScenes_CurrencyMenuItem',
  DELIVERY_CONDITIONS_BANNER = 'AEMScenes_DeliveryConditionsBanner',
  DISTINCTIVE_PRODUCT_DETAILS = 'AEMScenes_DistinctiveProductDetails',
  DPE_ACTIVATOR = 'AEMScenes_DpeActivatorContainer',
  ESSENTIALS = 'AEMScenes_Essentials',
  ETICKET = 'AEMScenes_Eticket',
  FEEFO_BRAND_BADGE = 'AEMScenes_FeefoBrandBadge',
  FOLDER_NAVIGATION = 'AEMScenes_FolderNavigation',
  GIFT_VOUCHER = 'AEMScenes_GiftVoucher',
  GLOSSARY = 'AEMScenes_Glossary',
  GUEST_REDIRECT = 'AEMScenes_GuestRedirect',
  LANGUAGE_MENU_ITEM = 'AEMScenes_LanguageMenuItem',
  LINE = 'AEMScenes_Line',
  LIST = 'AEMScenes_List',
  LOGIN = 'AEMScenes_Login',
  MOBILE_NAVIGATION = 'AEMScenes_MobileNavigation',
  MONETATE = 'AEMScenes_Monetate',
  NEWSLETTER_SUBSCRIBE = 'AEMScenes_NewsletterSubscribe',
  ORDER_OVERVIEW = 'AEMScenes_OrderOverview',
  PRODUCT_COMPARISON = 'AEMScenes_ProductComparison',
  PRODUCT_CONTENT_VIDEO = 'AEMScenes_ProductContentVideo',
  PRODUCT_DETAIL_V2 = 'AEMScenes_ProductDetail_v2',
  PRODUCT_GROUP = 'AEMScenes_ProductGroup',
  PRODUCT_GROUP_FREDHOPPER = 'AEMScenes_ProductGroupFredhopper',
  PRODUCT_GROUP_MONETATE = 'AEMScenes_ProductGroupMonetate',
  PRODUCT_GROUP_PDP_COMPARISON_FREDHOPPER = 'AEMScenes_ProductGroupPdpComparisonFredhopper',
  PRODUCT_GROUP_PDP_COMPARISON_MONETATE = 'AEMScenes_ProductGroupPdpComparisonMonetate',
  PRODUCT_INSPIRATION = 'AEMScenes_ProductInspiration',
  PRODUCT_LISTER = 'AEMScenes_ProductLister',
  PRODUCT_RECENTLY_VIEWED = 'AEMScenes_ProductRecentlyViewed',
  PRODUCT_RELEVANT_CONTENT = 'AEMScenes_ProductRelevantContent',
  PRODUCT_REVIEW = 'AEMScenes_ProductReview',
  PRODUCT_TOP_FIVE = 'AEMScene_ProductTopFive',
  PRODUCT_WIZARD = 'AEMScenes_ProductWizard',
  RECENTLY_OPENED_STORES = 'AEMScenes_RecentlyOpenedStores',
  REEVOO_CUSTOMER_EXPERIENCE = 'AEMScenes_ReevooCustomerExperience',
  REGISTRATION_COMPACT = 'AEMScenes_RegistrationCompact',
  RELATED_CATEGORIES = 'AEMScenes_RelatedCategories',
  RENTAL = 'AEMScenes_Rental',
  RETURN_ITEMS = 'AEMScenes_ReturnItems',
  RETURN_PORTAL_LOGIN = 'AEMScenes_ReturnPortalLogin',
  SEARCH_MENU_ITEM = 'AEMScenes_SearchMenuItem',
  AUTHOR_DATA = 'AEMScenes_AuthorData',
  AUTHOR_INFORMATION = 'AEMScenes_AuthorInformation',
  AUTHOR_LINKED_CONTENT_PAGES = 'AEMScenes_AuthorLinkedContentPages',
  AUTHOR_LINKED_PRODUCTS = 'AEMScenes_AuthorLinkedProducts',
  SERVICE_ORDER_TRACKING = 'AEMScenes_ServiceOrderTracking',
  SHOPPING_BUDGET = 'AEMScenes_ShoppingBudget',
  SHOP_NUMBER = 'AEMScenes_ShopNumber',
  SHOP_THE_LOOK = 'AEMScenes_ShopTheLook',
  SINGLE_PRODUCT_TILE = 'AEMScenes_SingleProductTile',
  SLIDER = 'AEMScenes_Slider',
  SOCIAL_LINKS = 'AEMScenes_SocialLinks',
  STORE_DEPARTMENTS = 'AEMScenes_StoreDepartments',
  STORE_GENERAL = 'AEMScenes_StoreGeneral',
  STORE_LOCATOR = 'AEMScenes_StoreLocator',
  STORE_NEARBY_STORES = 'AEMScenes_StoreNearbyStores',
  TEASER = 'AEMScenes_Teaser',
  VIDEO = 'AEMScenes_Video',
  VOUCHER = 'AEMScenes_Voucher',
  WISHLIST = 'AEMScenes_Wishlist',
  WISHLIST_MENU_ITEM = 'AEMScenes_WishlistMenuItem',
  ZITCHA_BANNER = 'AEMScenes_ZitchBanner',
  ACCOUNT_ENRICHMENT = 'AEMScenes_AccountEnrichment',
}

export type RenderData<Props = Record<string, unknown>> = {
  props?: Props;
  store?: Record<string, unknown>;
  context: {
    aemConfiguration: AemConfiguration;
    mainWebShopConfig?: MainWebShopConfiguration;
    abConfiguration?: ABConfiguration;
  };
  queryClientState?: DehydratedState;
};

export type HypernovaRenderStoreDataProductLister = {
  lister: ProductSearchObject;
  url: UrlState;
  filters: FilterState;
  productGroup?: ProductGroupById;
};

export type HypernovaTransportableData = RenderData & {
  loadByClient?: Array<string>;
  translation: I18nTranslationObject;
};

export type HypernovaJob = { name: AvailableRenderConfiguration['componentName']; data: RenderData };
export type HypernovaJobs = { [name: string]: HypernovaJob };

export type HypernovaResponse = {
  [name: string]: {
    error?: Error;
    html: string;
    job: HypernovaJob;
  };
};

export type HypernovaError<TData> = Error & { config?: { data?: string | StringTMap<TData> } };

export type ModuleImporter<T> = Promise<{
  default: T;
}>;

export type AvailableRenderConfiguration = {
  /** If set to true, the component will be rendered using the Hono renderer */
  honoRenderer?: boolean;
  /**
   * If set to true, the component will only be rendered on the server
   * and won't be included inside the client bundle
   */
  serverOnly?: boolean;
  clientOnly?: boolean;
  componentName: AEMScenes;
  hasReduxModule: boolean;
  reduxStorePerInstance?: boolean;
  /** If set to true, `a:display-contents` class will be added */
  displayContents?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  promisedComponent: () => ModuleImporter<ComponentType<any>>;
};

export type DynamicReduxModuleRootInstance = {
  hasRouter: boolean;
  modules: Array<ISagaModule<unknown>>;
};
export type DynamicReduxModuleRootInstanceSimple = () => DynamicReduxModuleRootInstance;
export type DynamicReduxModuleRootInstanceWithInitialData = (initialData?: unknown) => DynamicReduxModuleRootInstance;
export type DynamicReduxModuleRootInstanceWithInitialHistory = (history: History) => DynamicReduxModuleRootInstance;
export type RenderingToolsRedux = {
  configureStore: () => ModuleImporter<() => Promise<IStoreConfigurator>>;
};

export type RenderingToolsI18n = {
  instanceCreator: () => ModuleImporter<() => i18n>;
  instanceCreatorBackEnd: () => ModuleImporter<() => i18n>;
  frontEndInitOptions: () => Promise<{
    default: (version: string, translationLanguage?: string, translationEndpoint?: string) => InitOptions;
  }>;
  backendEndInitOptions: () => Promise<{
    default: (version: string, translationLanguage: string, ajaxOverride?: i18nBackendAjaxOverride) => InitOptions;
  }>;
};

export type RenderingTools = {
  redux: RenderingToolsRedux;
  i18n: RenderingToolsI18n;
  numeral: () => ModuleImporter<AsNumeralInterface>;
  cotopaxi: AvailableComponentLibraryTools;
  andes: Required<AvailableComponentLibraryTools>;
};

export type AvailableComponentLibraryTools = {
  version: () => Promise<{ default: () => string }>;
  preloadAssetFileGenerator?: () => Promise<{
    default: (basePath?: string) => StringStringArrayMap;
  }>;
};

export type VersionData = {
  react: string;
  cotopaxi: string;
  andes: string;
  andesAssets: StringStringArrayMap;
};
